// AIVIO timers: pure helpers for the «Таймеры» settings page.
// Semantics mirror fixes/timers/aivio_timers.rb (RuleBuilder / ActionRunner).

export const TRIGGERS = [
  'no_operator_reply',
  'bot_handoff_unclaimed',
  'customer_silent',
];

export const ACTIONS = [
  'notify',
  'add_private_note',
  'send_message',
  'add_label',
  'remove_label',
  'change_priority',
  'assign_agent',
  'assign_team',
  'remove_assigned_agent',
  'remove_assigned_team',
  'resolve_conversation',
  'snooze_conversation',
];

export const NO_PARAM_ACTIONS = [
  'remove_assigned_agent',
  'remove_assigned_team',
  'resolve_conversation',
  'snooze_conversation',
];

export const TEXT_ACTIONS = ['add_private_note', 'send_message'];
export const LABEL_ACTIONS = ['add_label', 'remove_label'];
export const PRIORITIES = ['low', 'medium', 'high', 'urgent', 'nil'];
export const MAX_TEXT = 2000;
export const DEFAULT_MAX_MINUTES = 30 * 24 * 60;

export const DURATION_UNITS = { minutes: 1, hours: 60, days: 1440 };

// i18n key fragment for a snake_case backend value: 'no_operator_reply' → 'NO_OPERATOR_REPLY'.
export const i18nKey = value => String(value || '').toUpperCase();

// ---------------------------------------------------------------- duration
// minutes → the largest unit that represents it exactly (90 → 90 min, 120 → 2 h, 2880 → 2 d).
export const minutesToDuration = minutes => {
  const value = Number(minutes);
  if (!Number.isInteger(value) || value <= 0) {
    return { value: null, unit: 'minutes' };
  }
  if (value % DURATION_UNITS.days === 0) {
    return { value: value / DURATION_UNITS.days, unit: 'days' };
  }
  if (value % DURATION_UNITS.hours === 0) {
    return { value: value / DURATION_UNITS.hours, unit: 'hours' };
  }
  return { value, unit: 'minutes' };
};

// number + unit → whole minutes, or null when empty/invalid. Fractions allowed (1.5 h → 90).
export const durationToMinutes = (value, unit) => {
  if (value === '' || value === null || value === undefined) return null;
  const factor = DURATION_UNITS[unit];
  const amount = Number(value);
  if (!factor || !Number.isFinite(amount) || amount <= 0) return null;
  const minutes = Math.round(amount * factor);
  return minutes >= 1 ? minutes : null;
};

export const isValidMinutes = (minutes, max = DEFAULT_MAX_MINUTES) =>
  Number.isInteger(minutes) && minutes >= 1 && minutes <= max;

// 15 → '15 мин', 240 → '4 ч', 90 → '1 ч 30 мин', 1500 → '1 д 1 ч'.
export const formatDuration = (minutes, units) => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total <= 0) return '';
  const { days: perDay, hours: perHour } = DURATION_UNITS;
  const days = Math.floor(total / perDay);
  const hours = Math.floor((total % perDay) / perHour);
  const mins = total % perHour;
  return [
    [days, units.days],
    [hours, units.hours],
    [mins, units.minutes],
  ]
    .filter(([amount]) => amount > 0)
    .map(([amount, unit]) => `${amount} ${unit}`)
    .join(' ');
};

export const shortUnits = t => ({
  days: t('AIVIO_TIMERS.UNITS_SHORT.DAYS'),
  hours: t('AIVIO_TIMERS.UNITS_SHORT.HOURS'),
  minutes: t('AIVIO_TIMERS.UNITS_SHORT.MINUTES'),
});

// «Нет ответа оператора 15 мин» / «Бот передал, никто не взял 10 мин» / «Клиент молчит после ответа 4 ч».
export const conditionSentence = (rule, t) => {
  const duration = formatDuration(rule?.minutes, shortUnits(t));
  if (!TRIGGERS.includes(rule?.trigger)) {
    return [rule?.trigger, duration].filter(Boolean).join(' ');
  }
  return t(`AIVIO_TIMERS.TRIGGERS.${i18nKey(rule.trigger)}.SENTENCE`, {
    duration,
  });
};

// ---------------------------------------------------------------- actions
export const defaultParams = actionName => {
  if (TEXT_ACTIONS.includes(actionName)) return [''];
  if (actionName === 'change_priority') return ['high'];
  if (actionName === 'notify') {
    return [{ user_ids: [], team_ids: [], text: '' }];
  }
  return [];
};

export const newAction = (actionName = 'notify') => ({
  action_name: actionName,
  action_params: defaultParams(actionName),
});

const cloneAction = action => ({
  action_name: action?.action_name || '',
  action_params: JSON.parse(JSON.stringify(action?.action_params ?? [])),
});

const truncate = (text, size = 40) => {
  const value = String(text || '').trim();
  return value.length > size ? `${value.slice(0, size - 1)}…` : value;
};

// One readable line per action. resolve = { agent(id), team(id), inbox(id) } → name or ''.
export const actionSummary = (action, t, resolve = {}) => {
  const name = action?.action_name;
  const params = Array.isArray(action?.action_params)
    ? action.action_params
    : [];
  const label = ACTIONS.includes(name)
    ? t(`AIVIO_TIMERS.ACTIONS.${i18nKey(name)}`)
    : name;
  const agent = id => resolve.agent?.(id) || `#${id}`;
  const team = id => resolve.team?.(id) || `#${id}`;
  let details = '';
  if (LABEL_ACTIONS.includes(name)) details = params.join(', ');
  else if (name === 'change_priority') {
    details = t(`AIVIO_TIMERS.PRIORITIES.${i18nKey(params[0])}`);
  } else if (name === 'assign_agent') {
    const value = params[0];
    if (value === 'last_responding_agent' || value === 'nil') {
      details = t(`AIVIO_TIMERS.AGENT_SPECIAL.${i18nKey(value)}`);
    } else if (value !== undefined && value !== '') details = agent(value);
  } else if (name === 'assign_team') {
    if (params[0] !== undefined && params[0] !== '') details = team(params[0]);
  } else if (TEXT_ACTIONS.includes(name)) {
    details = params[0] ? `«${truncate(params[0])}»` : '';
  } else if (name === 'notify') {
    const cfg = params[0] || {};
    details = [
      ...(cfg.user_ids || []).map(agent),
      ...(cfg.team_ids || []).map(team),
    ].join(', ');
  }
  return details ? `${label}: ${details}` : label;
};

// '#123' — conversation reference as shown in the dashboard.
export const conversationRef = displayId => `#${displayId}`;

// Log «result» is '; '-joined action names, optionally «name: note» (see ActionRunner#perform).
export const formatLogResult = (result, t) =>
  String(result || '')
    .split('; ')
    .filter(Boolean)
    .map(part => {
      const [head, ...rest] = part.split(': ');
      if (!ACTIONS.includes(head)) return part;
      const label = t(`AIVIO_TIMERS.ACTIONS.${i18nKey(head)}`);
      return rest.length ? `${label}: ${rest.join(': ')}` : label;
    })
    .join('; ');

// ---------------------------------------------------------------- form draft ↔ API payload
export const newDraft = () => ({
  id: null,
  name: '',
  enabled: true,
  mode: 'log',
  trigger: 'no_operator_reply',
  durationValue: 15,
  durationUnit: 'minutes',
  inbox_ids: [],
  respect_working_hours: true,
  actions: [newAction('notify')],
});

export const ruleToDraft = rule => {
  if (!rule) return newDraft();
  const { value, unit } = minutesToDuration(rule.minutes);
  return {
    id: rule.id,
    name: rule.name || '',
    enabled: !!rule.enabled,
    mode: rule.mode === 'live' ? 'live' : 'log',
    trigger: rule.trigger || 'no_operator_reply',
    durationValue: value,
    durationUnit: unit,
    inbox_ids: [...(rule.inbox_ids || [])],
    respect_working_hours: rule.respect_working_hours !== false,
    actions: (rule.actions || []).map(cloneAction),
  };
};

const cleanParams = action => {
  const { action_name: name, action_params: params = [] } = action;
  if (NO_PARAM_ACTIONS.includes(name)) return [];
  if (TEXT_ACTIONS.includes(name)) return [String(params[0] || '').trim()];
  if (name === 'notify') {
    const cfg = params[0] || {};
    return [
      {
        user_ids: [...(cfg.user_ids || [])],
        team_ids: [...(cfg.team_ids || [])],
        text: String(cfg.text || '').trim(),
      },
    ];
  }
  return [...params];
};

export const draftToPayload = draft => ({
  name: String(draft.name || '').trim(),
  enabled: !!draft.enabled,
  mode: draft.mode,
  trigger: draft.trigger,
  minutes: durationToMinutes(draft.durationValue, draft.durationUnit),
  inbox_ids: [...(draft.inbox_ids || [])],
  respect_working_hours: !!draft.respect_working_hours,
  actions: (draft.actions || []).map(action => ({
    action_name: action.action_name,
    action_params: cleanParams(action),
  })),
});

// Preview only depends on condition, duration and inboxes, but the backend validates the whole
// rule — send a harmless placeholder action so half-filled actions do not block «Проверить».
export const previewPayload = draft => {
  const payload = draftToPayload(draft);
  return {
    name: payload.name,
    trigger: payload.trigger,
    minutes: payload.minutes,
    inbox_ids: payload.inbox_ids,
    actions: [{ action_name: 'resolve_conversation', action_params: [] }],
  };
};

// 422 body → list of messages ({ errors: [...] } from the timers controller, { error } / { message } otherwise).
export const extractErrors = error => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map(String);
  }
  if (data?.error) return [String(data.error)];
  if (data?.message) return [String(data.message)];
  return [];
};
